import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { TrackClick } from '@g4/trackster';
import { withValue, hook } from '@g4/automation-hooks';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';

import { getPrice } from '@g4/next-furniture/Price/Price';
import { Box, Text, useMedia, VerticalSpacing } from '../../../Boneyard';
import Markdown from '../../../Markdown';
import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
import useUplift from '../../../../hooks/useUplift';
import { useUpliftState } from '../../../UpliftStateProvider';

import UpLift from '../../../UpLift';
import PayLaterMessage from '../../../../components/paypal/PayLaterMessage';

import UPLIFT_LOGO from '../../../../images/ic-uplift.svg';
import UPLIFT_LOGO_WHITE from '../../../../images/ic-uplift-white.svg';
import INFO_ICON from '../../../../images/info.svg';
import INFO_ICON_WHITE from '../../../../images/info-white.svg';

import { usePaypalState } from '../../../../components/PaypalStateProvider';

import {
  CART_UPLIFT,
  CART_TOTAL,
  CART_UPLIFT_TEXT,
  CART_DUE_TODAY,
  CART_DUE_AT_HOTEL_CHECKOUT,
  CART_PAYPAL,
} from '../../../../../automationHooks.json';

const propTypes = {
  theme: PropTypes.object,
  totalDueToday: PropTypes.number.isRequired,
  totalDueAtHotelCheckout: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  hidePrice: PropTypes.bool,
  isUpliftInitialised: PropTypes.bool,
  setIsUpliftInitialised: PropTypes.func,
  paymentDueAt: PropTypes.string,
  showUplift: PropTypes.bool,
  isPaymentStep: PropTypes.bool,
};

const defaultProps = {};

const UpliftButton = styled.button.attrs({
  type: 'button',
})`
  display: flex;
  align-items: flex-start;
  text-align: left;
`;

const TooltipIconContainer = styled.span`
  position: absolute;
  display: inline;
  width: 15px;
  height: 15px;
  align-self: center;
  ${props => props.theme.query.md} {
    width: 20px;
    height: 20px;
  }
`;

const InfoIcon = styled.span`
  display: block;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  height: 22px;
  width: 22px;
  background: url(${props =>
    props.payAtHotelCheckout ? INFO_ICON_WHITE : INFO_ICON});
  background-size: contain;
  ${props => props.theme.query.md} {
    height: 24px;
    width: 24px;
  }
`;

const UpliftLogo = styled.div`
  height: 16px;
  width: 60px;
  background: url(${props =>
    props.payAtHotelCheckout ? UPLIFT_LOGO_WHITE : UPLIFT_LOGO});
  background-size: contain;
  background-repeat: no-repeat;
  flex-shrink: 0;
  margin-right: 7px;
`;

const CartFooter = ({
  theme,
  total,
  totalDueToday,
  totalDueAtHotelCheckout,
  hidePrice,
  isUpliftInitialised,
  setIsUpliftInitialised,
  paymentDueAt,
  showUplift,
  isPaymentStep,
}) => {
  const payAtHotelCheckout = paymentDueAt === 'HOTEL_CHECKOUT';

  const translations = useTranslations();
  const {
    upliftText,
    tripTotalLabel,
    dueTodayLabel,
    dueToHotelLabelHTML,
  } = translations.cart;
  const mq = useMedia();

  const { isUpliftAvailable } = useUpliftState();

  useUplift({
    isUpliftInitialised,
    setIsUpliftInitialised,
  });

  const { isPaypalAvailable, paypalBannersEnabled } = usePaypalState();

  const { isCallCenterAgent, isTravelAgent, isStationAgent } = useUserContext();
  const isAgent = isCallCenterAgent || isTravelAgent || isStationAgent;

  const UpliftSection = () => (
    <UpLift
      id="uplift-cart-banner"
      value={totalDueToday.toString()}
      type="total"
      model="total"
      hidden={!isUpliftAvailable || !showUplift}
    >
      {(UpliftMonthlyAmount, tooltip) => (
        <>
          <VerticalSpacing size={3} />
          <TrackClick
            category="Site Click Tracking"
            action="Trip Summary"
            label="Uplift PayMonthly message"
          >
            <UpliftButton {...hook(CART_UPLIFT.hook, 'button')}>
              <UpliftLogo
                payAtHotelCheckout={payAtHotelCheckout}
                {...hook(CART_UPLIFT.hook, 'logo')}
              />
              <Text
                size={14}
                color={
                  payAtHotelCheckout
                    ? theme.colors.cart.payAtHotelText
                    : theme.colors.cart.upliftText
                }
                automationHook={CART_UPLIFT_TEXT.hook}
              >
                <Markdown
                  source={upliftText}
                  customElements={{
                    UpliftMonthlyAmount,
                  }}
                />
                <TooltipIconContainer role="img" aria-label="info icon">
                  {tooltip}
                  <InfoIcon payAtHotelCheckout={payAtHotelCheckout} />
                </TooltipIconContainer>
              </Text>
            </UpliftButton>
          </TrackClick>
        </>
      )}
    </UpLift>
  );

  return (
    <div>
      <Box
        p={mq.lt('md') ? 2 : 3}
        pr={mq.lt('md') ? 4 : 6}
        pl={mq.lt('md') ? 4 : 6}
        backgroundColor={theme.colors.cart.primaryBackground}
      >
        <Box w={'100%'} d="flex" justifyContent="space-between">
          <Text
            uppercase
            weight={500}
            size={mq.lt('md') ? 18 : 22}
            automationHook={withValue(CART_TOTAL.hook, 'title')}
          >
            {tripTotalLabel}
          </Text>
          <Text
            weight={500}
            size={mq.lt('md') ? 18 : 22}
            automationHook={withValue(CART_TOTAL.hook, 'value')}
          >
            {hidePrice ? '$ --.--' : getPrice(total).price}
          </Text>
        </Box>
        {!hidePrice && !payAtHotelCheckout && <UpliftSection />}
      </Box>
      {payAtHotelCheckout && (
        <Box
          p={mq.lt('md') ? 2 : 3}
          pr={mq.lt('md') ? 4 : 6}
          pl={mq.lt('md') ? 4 : 6}
          backgroundColor={theme.colors.cart.payAtHotelBackground}
        >
          <Box w={'100%'} top={10} d="flex" justifyContent="space-between">
            <Text
              weight={800}
              size={mq.lt('md') ? 18 : 22}
              automationHook={withValue(CART_DUE_TODAY.hook, 'title')}
              color={theme.colors.cart.payAtHotelText}
            >
              {dueTodayLabel}
            </Text>
            <Text
              weight={800}
              size={mq.lt('md') ? 16 : 20}
              automationHook={withValue(CART_DUE_TODAY.hook, 'value')}
              color={theme.colors.cart.payAtHotelText}
            >
              {hidePrice ? '$ --.--' : getPrice(totalDueToday).price}
            </Text>
          </Box>

          <VerticalSpacing size={2} />

          <Box w={'100%'} top={10} d="flex" justifyContent="space-between">
            <Text
              weight={500}
              size={mq.lt('md') ? 12 : 16}
              automationHook={withValue(
                CART_DUE_AT_HOTEL_CHECKOUT.hook,
                'title'
              )}
              color={theme.colors.cart.payAtHotelText}
            >
              <Markdown source={dueToHotelLabelHTML} />
            </Text>
            <Text
              weight={500}
              size={mq.lt('md') ? 12 : 16}
              automationHook={withValue(
                CART_DUE_AT_HOTEL_CHECKOUT.hook,
                'value'
              )}
              color={theme.colors.cart.payAtHotelText}
            >
              {hidePrice ? '$ --.--' : getPrice(totalDueAtHotelCheckout).price}
            </Text>
          </Box>
          {!hidePrice && payAtHotelCheckout && <UpliftSection />}
        </Box>
      )}

      {isPaypalAvailable && !isPaymentStep && !isAgent && paypalBannersEnabled && (
        <Box
          pt={2}
          pb={hidePrice ? 2 : 0}
          pr={mq.lt('md') ? 4 : 6}
          pl={mq.lt('md') ? 4 : 6}
          backgroundColor={theme.colors.cart.primaryBackground}
          automationHook={CART_PAYPAL.hook}
        >
          <PayLaterMessage
            total={hidePrice ? 0 : totalDueToday}
            section={'cart'}
          />
        </Box>
      )}
    </div>
  );
};

CartFooter.propTypes = propTypes;
CartFooter.defaultProps = defaultProps;

export default withTheme(CartFooter);
